/**
 * useVarAutocomplete.ts — hook that listens to contentEditable input/keydown events
 * on a canvas root ref, detects the "${partial" trigger, and exposes menu state
 * plus handlers for keyboard navigation, picking, and closing.
 *
 * Caller is responsible for rendering <VarAutocompleteMenu> when open=true
 * and calling pick(i) / close() from menu events.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { VarDef } from "./emailVars";
import { matchTrigger, filterCatalog, getCaretRect } from "./varAutocomplete";

export interface MenuState {
	open: boolean;
	items: VarDef[];
	/** Currently highlighted index */
	index: number;
	/** Viewport rect used to position the menu (bottom-left of caret) */
	rect: DOMRect | null;
}

const CLOSED: MenuState = { open: false, items: [], index: 0, rect: null };

/** Stored trigger context so pick() knows what text range to delete. */
interface TriggerCtx {
	node: Text;
	tokenStart: number;
	tokenEnd: number;
	host: HTMLElement;
}

export interface UseVarAutocompleteResult {
	menu: MenuState;
	/** Pass to canvas root onKeyDown (capture phase) to handle ↑↓ Enter Esc */
	onKeyDown: (e: React.KeyboardEvent | KeyboardEvent) => void;
	/** Pick item at index i — deletes typed "${partial" and inserts chip via callback */
	pick: (i: number) => void;
	/** Close menu without inserting */
	close: () => void;
}

export function useVarAutocomplete(
	canvasRootRef: React.RefObject<HTMLElement | null>,
	catalog: VarDef[],
	insertVarAtRange: (varName: string, label: string, deleteRange: Range) => void
): UseVarAutocompleteResult {
	const [menu, setMenu] = useState<MenuState>(CLOSED);
	const triggerRef = useRef<TriggerCtx | null>(null);

	const close = useCallback(() => {
		triggerRef.current = null;
		setMenu(CLOSED);
	}, []);

	/** Handle native input events on the canvas root */
	const handleInput = useCallback(() => {
		const sel = window.getSelection();
		if (!sel || !sel.rangeCount) {
			close();
			return;
		}
		const range = sel.getRangeAt(0);
		if (!range.collapsed) {
			close();
			return;
		}
		const node = range.startContainer;
		if (node.nodeType !== Node.TEXT_NODE) {
			close();
			return;
		}
		const textNode = node as Text;
		const host = textNode.parentElement?.closest("[data-el]") as HTMLElement | null;
		if (!host) {
			close();
			return;
		}
		const off = range.startOffset;
		const textBefore = textNode.textContent?.slice(0, off) ?? "";
		const match = matchTrigger(textBefore);
		if (!match) {
			close();
			return;
		}
		const items = filterCatalog(catalog, match.partial);
		triggerRef.current = {
			node: textNode,
			tokenStart: match.tokenStart,
			tokenEnd: match.tokenEnd,
			host,
		};
		const rect = getCaretRect(range, host);
		setMenu({ open: true, items, index: 0, rect });
	}, [catalog, close]);

	/** Handle keydown: ↑↓ navigate, Enter pick, Esc close */
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent | KeyboardEvent) => {
			if (!triggerRef.current) return;
			if (e.key === "Escape") {
				e.preventDefault();
				close();
				return;
			}
			if (e.key === "ArrowDown" || e.key === "ArrowUp") {
				e.preventDefault();
				setMenu((prev) => {
					if (!prev.open || !prev.items.length) return prev;
					const n = prev.items.length;
					const next = (prev.index + (e.key === "ArrowDown" ? 1 : -1) + n) % n;
					return { ...prev, index: next };
				});
				return;
			}
			if (e.key === "Enter") {
				setMenu((prev) => {
					if (!prev.open || !prev.items.length) return prev;
					// schedule pick so we have the latest index
					const ctx = triggerRef.current;
					if (!ctx) return prev;
					const v = prev.items[prev.index];
					if (!v) return prev;
					// build delete range for the typed "${partial" text
					const deleteRange = document.createRange();
					deleteRange.setStart(ctx.node, ctx.tokenStart);
					deleteRange.setEnd(ctx.node, ctx.tokenEnd);
					insertVarAtRange(v.n, v.d, deleteRange);
					triggerRef.current = null;
					return CLOSED;
				});
				e.preventDefault();
				return;
			}
		},
		[close, insertVarAtRange]
	);

	const pick = useCallback(
		(i: number) => {
			const ctx = triggerRef.current;
			if (!ctx) return;
			setMenu((prev) => {
				const v = prev.items[i];
				if (!v) return CLOSED;
				const deleteRange = document.createRange();
				deleteRange.setStart(ctx.node, ctx.tokenStart);
				deleteRange.setEnd(ctx.node, ctx.tokenEnd);
				insertVarAtRange(v.n, v.d, deleteRange);
				triggerRef.current = null;
				return CLOSED;
			});
		},
		[insertVarAtRange]
	);

	// Attach native input listener on canvas root (captures input from all
	// descendant contentEditables without needing to thread per-field handlers)
	useEffect(() => {
		const root = canvasRootRef.current;
		if (!root) return;
		root.addEventListener("input", handleInput);
		return () => {
			root.removeEventListener("input", handleInput);
		};
	}, [canvasRootRef, handleInput]);

	// Click-away to close
	useEffect(() => {
		if (!menu.open) return;
		const handler = (e: MouseEvent) => {
			const target = e.target as Element | null;
			if (!target) return;
			// keep open if clicking inside the menu or an editable
			if (target.closest("[data-var-menu]") || target.closest("[data-el]")) return;
			close();
		};
		document.addEventListener("mousedown", handler);
		return () => {
			document.removeEventListener("mousedown", handler);
		};
	}, [menu.open, close]);

	return { menu, onKeyDown, pick, close };
}
